/**
 * dateUtils.js
 * Utilitaires pour la manipulation des dates
 */

const DateUtils = (() => {
  /**
   * Formatter une date ISO (YYYY-MM-DD) au format français
   */
  function formatDate(isoDate, format = 'DD/MM/YYYY') {
    const date = new Date(isoDate + 'T00:00:00');
    
    if (isNaN(date.getTime())) {
      return isoDate;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    if (format === 'DD/MM/YYYY') {
      return `${day}/${month}/${year}`;
    }
    if (format === 'ISO') {
      return `${year}-${month}-${day}`;
    }
    if (format === 'FR') {
      return date.toLocaleDateString('fr-FR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }

    return isoDate;
  }

  /**
   * Convertir date française (DD/MM/YYYY) en ISO (YYYY-MM-DD)
   */
  function frenchToISO(frenchDate) {
    const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = frenchDate.match(regex);
    
    if (!match) return null;

    const day = String(match[1]).padStart(2, '0');
    const month = String(match[2]).padStart(2, '0');
    const year = match[3];

    return `${year}-${month}-${day}`;
  }

  /**
   * Obtenir la date d'aujourd'hui au format ISO
   */
  function today() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }

  /**
   * Obtenir le début du mois courant
   */
  function monthStart() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  }

  /**
   * Obtenir la fin du mois courant
   */
  function monthEnd() {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    next.setDate(next.getDate() - 1);
    return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
  }

  /**
   * Calculer nombre de jours entre deux dates ISO
   */
  function daysBetween(startISO, endISO) {
    const start = new Date(startISO);
    const end = new Date(endISO);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // Inclure les deux jours
    return diffDays;
  }

  /**
   * Générer plage de dates (pour range picker)
   */
  function getLastNDays(n) {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - n);

    return {
      start: formatDate(start.toISOString().split('T')[0], 'ISO'),
      end: formatDate(end.toISOString().split('T')[0], 'ISO')
    };
  }

  /**
   * Vérifier si date est valide
   */
  function isValidDate(isoDate) {
    const date = new Date(isoDate + 'T00:00:00');
    return !isNaN(date.getTime());
  }

  /**
   * Get the Monday (ISO string) of the week containing a given date
   */
  function getMonday(isoDate) {
    const d = new Date(isoDate + 'T00:00:00');
    const day = d.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }

  /**
   * Get all work weeks (Mon-Fri) between two ISO dates
   * Returns array of { mondayISO, label }
   */
  function getWeeksInRange(startISO, endISO) {
    const weeks = [];
    const end = new Date(endISO + 'T00:00:00');
    let monISO = getMonday(startISO);
    let current = new Date(monISO + 'T00:00:00');

    while (current <= end) {
      const friday = new Date(current);
      friday.setDate(friday.getDate() + 4);
      const fLabel = friday.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const mLabel = current.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
      weeks.push({
        mondayISO: monISO,
        label: `${mLabel} au ${fLabel}`
      });
      current.setDate(current.getDate() + 7);
      monISO = `${current.getFullYear()}-${String(current.getMonth()+1).padStart(2,'0')}-${String(current.getDate()).padStart(2,'0')}`;
    }
    return weeks;
  }

  return {
    formatDate,
    frenchToISO,
    today,
    monthStart,
    monthEnd,
    daysBetween,
    getLastNDays,
    isValidDate,
    getMonday,
    getWeeksInRange
  };
})();

export default DateUtils;
